import { useEffect, useState, type ReactNode } from "react";
import { createAppStore, type AppStore } from "@otrack/app-shell";
import type { LocalStore } from "@otrack/local-store";
import { WebLocalStore } from "@otrack/local-store-web";
import { AuthProblem, Splash } from "./auth-screens";

type OpenedApp = { store: AppStore; local: LocalStore } | "failed" | null;

/** Opens a device database and the app's store over it, and closes it when no longer shown. */
function useLocalApp(database: () => Promise<string>): OpenedApp {
  const [opened, setOpened] = useState<OpenedApp>(null);
  useEffect(() => {
    let cancelled = false;
    let local: WebLocalStore | null = null;
    database().then(async (name) => {
      if (cancelled) return;
      local = new WebLocalStore(name);
      const store = await createAppStore(local);
      if (!cancelled) setOpened({ store, local });
    }).catch((error: unknown) => {
      if (cancelled) return;
      console.error(error);
      setOpened("failed");
    });
    return () => {
      cancelled = true;
      void local?.close();
    };
  }, [database]);
  return opened;
}

export function LocalApp({ database, children }: { database: () => Promise<string>; children: (store: AppStore, local: LocalStore) => ReactNode }) {
  const opened = useLocalApp(database);
  if (opened === "failed") return <AuthProblem title="ontrack couldn't open its data">Check that this device allows site storage, then try again.</AuthProblem>;
  if (!opened) return <Splash/>;
  return children(opened.store, opened.local);
}
